import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import PinButton from "../components/pins/PinButton";
import useContentFontScale from "../hooks/useContentFontScale";
import Button from "../ui/Button";
import DrawerA11yWrapper from "../ui/DrawerA11yWrapper";
import Input from "../ui/Input";
import Select from "../ui/Select";
import Textarea from "../ui/Textarea";
import StatusBadge from "../ui/StatusBadge";
import { colors, radius, shadows, spacing, typography } from "../ui/tokens";

const overlayStyle = {
  background: "rgba(15,23,42,0.45)",
  zIndex: 1000,
};

const drawerStyle = {
  width: "min(460px, 100vw)",
  maxWidth: "100%",
  background: colors.surface,
  boxShadow: "-10px 0 28px rgba(15,23,42,0.22)",
  padding: spacing.xl + spacing.sm,
  overflowY: "auto",
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
};

const headerStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: spacing.sm,
};

const headerTextWrapStyle = {
  display: "grid",
  gap: spacing.xs,
};

const headerTitleStyle = {
  margin: 0,
  fontSize: typography.size.lg,
  lineHeight: typography.lineHeight.compact,
  color: colors.text,
  fontWeight: typography.weight.bold,
};

const headerSubtitleStyle = {
  margin: 0,
  fontSize: typography.size.xs,
  color: colors.textMuted,
};

const sectionStackStyle = {
  marginTop: spacing.md,
  display: "grid",
  gap: spacing.md,
};

const sectionCardStyle = {
  border: `1px solid ${colors.border}`,
  borderRadius: radius.md,
  background: colors.surfaceSubtle,
  padding: spacing.md + spacing.xs,
  display: "grid",
  gap: spacing.sm,
};

const sectionTitleStyle = {
  margin: 0,
  fontSize: typography.size.sm,
  color: colors.text,
  fontWeight: typography.weight.medium,
};

const actionRowStyle = {
  display: "flex",
  gap: spacing.sm,
  flexWrap: "wrap",
};

const fieldGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
  gap: spacing.sm,
};

const metadataGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
  gap: spacing.sm,
};

const metaItemStyle = {
  display: "grid",
  gap: spacing.xs,
};

const metaLabelStyle = {
  fontSize: typography.size.xs,
  color: colors.textMuted,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
};

const metaValueStyle = {
  fontSize: typography.size.sm,
  color: colors.text,
  fontWeight: typography.weight.medium,
};

const mutedBodyStyle = {
  margin: 0,
  fontSize: typography.size.sm,
  color: colors.textMuted,
  lineHeight: typography.lineHeight.reading,
};

const evidenceListStyle = {
  display: "grid",
  gap: spacing.sm,
};

const evidenceItemStyle = {
  border: `1px solid ${colors.border}`,
  borderRadius: radius.sm,
  background: colors.surface,
  padding: spacing.sm,
  display: "grid",
  gap: spacing.xs,
};

const evidenceMetaStyle = {
  margin: 0,
  fontSize: typography.size.xs,
  color: colors.textSubtle,
};

const historyItemStyle = {
  border: `1px solid ${colors.border}`,
  borderRadius: radius.sm,
  background: colors.surface,
  padding: spacing.sm,
  display: "grid",
  gap: spacing.xs,
  boxShadow: shadows.xs,
};

const errorStyle = {
  margin: 0,
  color: colors.danger,
  fontSize: typography.size.xs,
};

const actionBarStyle = {
  display: "flex",
  gap: spacing.sm,
  flexWrap: "wrap",
  justifyContent: "flex-end",
  paddingTop: spacing.sm,
  borderTop: `1px solid ${colors.border}`,
};

export default function TaskDetailsDrawer({ task, onClose, onSave, onComplete, onAddEvidence, readOnly = false }) {
  const isNew = !task?.id;
  const { scale: contentFontScale } = useContentFontScale();
  const titleId = useId();
  const closeRef = useRef(null);
  const [editMode, setEditMode] = useState(isNew && !readOnly);
  const [draft, setDraft] = useState(
    task || {
      id: null,
      title: "",
      description: "",
      controlId: "",
      framework: "SOC2",
      status: "Not started",
      priority: "Medium",
      dueDate: new Date().toISOString().slice(0, 10),
      owner: "unassigned",
    }
  );
  const [error, setError] = useState("");
  const [evidenceLabel, setEvidenceLabel] = useState("");
  const [evidenceUrl, setEvidenceUrl] = useState("");
  const [evidenceNote, setEvidenceNote] = useState("");
  const [evidenceError, setEvidenceError] = useState("");

  useEffect(() => {
    if (!task) return;
    setDraft(task);
    setEditMode(!task.id && !readOnly);
    setError("");
    setEvidenceLabel("");
    setEvidenceUrl("");
    setEvidenceNote("");
    setEvidenceError("");
  }, [task, readOnly]);

  const frameworks = useMemo(() => {
    const base = ["SOC2", "ISO27001", "Custom"];
    if (task?.framework && !base.includes(task.framework)) {
      return [task.framework, ...base];
    }
    return base;
  }, [task?.framework]);
  const statuses = useMemo(() => ["Not started", "In progress", "Needs review", "Completed"], []);
  const priorities = useMemo(() => ["Critical", "High", "Medium", "Low"], []);

  const updateField = (field, value) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    if (!draft.title?.trim()) {
      setError("Title is required.");
      return;
    }

    const normalized = {
      ...draft,
      title: draft.title.trim(),
      description: (draft.description || "").trim(),
      controlId: (draft.controlId || "").trim(),
      owner: (draft.owner || "").trim() || "unassigned",
    };

    onSave?.(normalized);
    setError("");
    setEditMode(false);
  };

  const handleCancelEdit = () => {
    if (isNew) {
      onClose();
      return;
    }
    setDraft(task);
    setEditMode(false);
    setError("");
  };

  const handleComplete = () => {
    if (!task?.id || task.status === "Completed") return;
    onComplete?.(task.id);
    setEditMode(false);
  };

  const handleAddEvidence = () => {
    if (!task?.id) return;
    if (!evidenceLabel.trim() && !evidenceUrl.trim() && !evidenceNote.trim()) {
      setEvidenceError("Add a label, link, or note.");
      return;
    }

    onAddEvidence?.(task.id, {
      label: evidenceLabel.trim(),
      url: evidenceUrl.trim(),
      note: evidenceNote.trim(),
    });

    setEvidenceLabel("");
    setEvidenceUrl("");
    setEvidenceNote("");
    setEvidenceError("");
  };

  const historyEntries = [...(task?.history || [])].sort(
    (a, b) => new Date(b.at).getTime() - new Date(a.at).getTime()
  );

  const evidenceDisabled = readOnly || !task?.id;

  if (!task) {
    return null;
  }

  const drawerSubtitle = editMode
    ? isNew
      ? "Create a task and assign ownership."
      : "Edit task details and save updates."
    : "Review task details, evidence, and activity.";

  return (
    <DrawerA11yWrapper
      open={Boolean(task)}
      onClose={onClose}
      labelledBy={titleId}
      initialFocusRef={closeRef}
      backdropStyle={overlayStyle}
      containerStyle={drawerStyle}
    >
      <div
        style={{
          fontSize: "calc(1rem * var(--content-font-scale))",
          "--content-font-scale": String(contentFontScale),
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div style={headerStyle}>
          <div style={headerTextWrapStyle}>
            <h3 id={titleId} style={headerTitleStyle}>
              {editMode ? (isNew ? "Create Task" : "Edit Task") : task.title}
            </h3>
            <p style={headerSubtitleStyle}>{drawerSubtitle}</p>
          </div>
          <div style={{ display: "flex", gap: spacing.sm, alignItems: "flex-start", flexWrap: "wrap", justifyContent: "flex-end" }}>
            {!isNew && task?.id ? (
              <PinButton
                itemType="action"
                itemId={task.id}
                summary={{
                  title: task.title || `Task #${task.id}`,
                  moduleLabel: "Action",
                  secondaryText: task.framework || null,
                  status: task.status || null,
                  ownerDisplay: task.owner || null,
                  updatedAt: task.dueDate || null,
                }}
              />
            ) : null}
            <Button
              onClick={onClose}
              variant="ghost"
              aria-label="Close task drawer"
              ref={closeRef}
              style={{ padding: `${spacing.xs}px ${spacing.sm}px`, minWidth: 32 }}
            >
              X
            </Button>
          </div>
        </div>

        {!editMode && (
          <div style={{ ...actionRowStyle, marginTop: spacing.sm }}>
            <Button disabled={readOnly} onClick={() => setEditMode(true)} variant="secondary">
              Edit
            </Button>
            {task.status !== "Completed" && (
              <Button disabled={readOnly} onClick={handleComplete} variant="primary">
                Mark complete
              </Button>
            )}
          </div>
        )}

        {editMode ? (
          <div style={sectionStackStyle}>
            <div style={sectionCardStyle}>
              <h4 style={sectionTitleStyle}>Task details</h4>
              <Input
                label="Title"
                value={draft.title}
                onChange={(e) => updateField("title", e.target.value)}
                placeholder="e.g. Quarterly Access Review"
              />
              <Textarea
                label="Description"
                value={draft.description}
                onChange={(e) => updateField("description", e.target.value)}
                placeholder="Describe the task objectives and requirements"
                style={{ minHeight: 96 }}
              />
              <div style={fieldGridStyle}>
                <Input
                  label="Control ID"
                  value={draft.controlId}
                  onChange={(e) => updateField("controlId", e.target.value)}
                  placeholder="AC-01"
                />
                <Select
                  label="Framework"
                  value={draft.framework}
                  onChange={(e) => updateField("framework", e.target.value)}
                >
                  {frameworks.map((f) => (
                    <option key={f}>{f}</option>
                  ))}
                </Select>
              </div>
              <div style={fieldGridStyle}>
                <Select
                  label="Status"
                  value={draft.status}
                  onChange={(e) => updateField("status", e.target.value)}
                >
                  {statuses.map((s) => (
                    <option key={s}>{s}</option>
                  ))}
                </Select>
                <Select
                  label="Priority"
                  value={draft.priority}
                  onChange={(e) => updateField("priority", e.target.value)}
                >
                  {priorities.map((p) => (
                    <option key={p}>{p}</option>
                  ))}
                </Select>
              </div>
              <div style={fieldGridStyle}>
                <Input
                  label="Due date"
                  type="date"
                  value={draft.dueDate?.slice(0, 10) || ""}
                  onChange={(e) => updateField("dueDate", e.target.value)}
                />
                <Input
                  label="Owner"
                  value={draft.owner}
                  onChange={(e) => updateField("owner", e.target.value)}
                  placeholder="owner@company.com"
                />
              </div>
            </div>

            {error && (
              <p role="alert" style={errorStyle}>
                {error}
              </p>
            )}

            <div style={actionBarStyle}>
              <Button onClick={handleCancelEdit} variant="secondary">
                Cancel
              </Button>
              <Button disabled={readOnly} onClick={handleSave} variant="primary">
                Save
              </Button>
            </div>
          </div>
        ) : (
          <div style={sectionStackStyle}>
            <div style={sectionCardStyle}>
              <div style={{ display: "flex", flexWrap: "wrap", gap: spacing.xs }}>
                <StatusBadge variant="status" value={task.status} fallbackLabel={task.status || "Unknown"} />
                <StatusBadge variant="severity" value={task.priority} fallbackLabel={task.priority || "Unknown"} />
                <StatusBadge variant="status" value={task.framework} fallbackLabel={task.framework || "Unknown"} />
              </div>

              <div style={metadataGridStyle}>
                <div style={metaItemStyle}>
                  <span style={metaLabelStyle}>Control</span>
                  <span style={metaValueStyle}>{task.controlId || "-"}</span>
                </div>
                <div style={metaItemStyle}>
                  <span style={metaLabelStyle}>Due</span>
                  <span style={metaValueStyle}>
                    {task.dueDate ? new Date(task.dueDate).toLocaleDateString() : "-"}
                  </span>
                </div>
                <div style={metaItemStyle}>
                  <span style={metaLabelStyle}>Owner</span>
                  <span style={metaValueStyle}>{task.owner || "unassigned"}</span>
                </div>
              </div>
            </div>

            <div style={sectionCardStyle}>
              <h4 style={sectionTitleStyle}>Description</h4>
              <p style={mutedBodyStyle}>{task.description || "No description provided."}</p>
            </div>

            <div style={sectionCardStyle}>
              <h4 style={sectionTitleStyle}>Evidence</h4>

              {task.evidence && task.evidence.length > 0 ? (
                <div style={evidenceListStyle}>
                  {task.evidence.map((ev) => (
                    <div key={ev.id} style={evidenceItemStyle}>
                      <div style={{ fontWeight: typography.weight.medium, color: colors.text }}>
                        {ev.label}
                      </div>
                      {ev.url && (
                        <a href={ev.url} target="_blank" rel="noreferrer" style={{ color: colors.accent }}>
                          {ev.url}
                        </a>
                      )}
                      {ev.note && <p style={{ ...mutedBodyStyle, marginTop: 0 }}>{ev.note}</p>}
                      <p style={evidenceMetaStyle}>
                        {ev.addedAt ? new Date(ev.addedAt).toLocaleString() : "Date unavailable"}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={mutedBodyStyle}>No evidence added yet.</p>
              )}

              <div style={fieldGridStyle}>
                <Input
                  label="Evidence label"
                  value={evidenceLabel}
                  onChange={(e) => setEvidenceLabel(e.target.value)}
                  placeholder="Evidence label"
                />
                <Input
                  label="Evidence link"
                  type="url"
                  value={evidenceUrl}
                  onChange={(e) => setEvidenceUrl(e.target.value)}
                  placeholder="Link (https://...)"
                />
              </div>
              <Textarea
                label="Evidence note"
                value={evidenceNote}
                onChange={(e) => setEvidenceNote(e.target.value)}
                placeholder="Optional note"
                style={{ minHeight: 72 }}
              />
              {evidenceError && (
                <p role="alert" style={errorStyle}>
                  {evidenceError}
                </p>
              )}
              <Button onClick={handleAddEvidence} disabled={evidenceDisabled} variant="primary" style={{ width: "100%" }}>
                {evidenceDisabled ? "Save task to add evidence" : "Add evidence"}
              </Button>
            </div>

            <div style={sectionCardStyle}>
              <h4 style={sectionTitleStyle}>History</h4>
              {historyEntries.length === 0 ? (
                <p style={mutedBodyStyle}>No history yet.</p>
              ) : (
                <div style={evidenceListStyle}>
                  {historyEntries.map((h) => (
                    <div key={h.id} style={historyItemStyle}>
                      <div style={{ fontWeight: typography.weight.medium, color: colors.text }}>{h.action}</div>
                      {h.detail && <p style={{ ...mutedBodyStyle, marginTop: 0 }}>{h.detail}</p>}
                      <p style={evidenceMetaStyle}>{new Date(h.at).toLocaleString()}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </DrawerA11yWrapper>
  );
}
